import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sessionEnd, showSnackbar } from "../../redux/actions";
import { RootState } from "../../redux/reducers";

// Material UI
import { 
  Box, Button, Chip, Divider, Typography, Card, Stack, 
  Switch, Tooltip, alpha, styled, useTheme 
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridRowsProp,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  getGridNumericOperators,
  getGridStringOperators,
} from "@mui/x-data-grid";

// Icons & Components
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';
import EuroIcon from '@mui/icons-material/Euro';
import CustomNoRowsOverlay from "../../components/CustomNoRowsOverlay";
import { getAll } from "../../fetchs/get";
import { ILot } from "../../interface/lot";
import { IProject } from "../../interface/project";
import PersonIcon from '@mui/icons-material/Person';


const FK_STATUT = {
  BROUILLON: 0,
  VALIDE: 1,
  CLOS: 2,
};


// --- STYLES ---
const StatCard = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  textAlign: 'center',
  minWidth: '120px',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
  }
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontWeight: 700,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f1f5f9',
  },
}));

// Formatters
const formatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const formatter2 = new Intl.NumberFormat("fr-FR", { style: "decimal", maximumFractionDigits: 1 });

export default function ProjectCostsSummary({ project, setLotId, setOpenModal }: any) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isClos = Number(project?.syncMetadata?.fkStatut) === FK_STATUT.CLOS;
  const token = useSelector((state: RootState) => state.user.token);
  const [ArrayLot, setArrayLot] = useState<ILot[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [isSwitchChecked, setIsSwitchChecked] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "name", sort: "asc" }]);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

  // --- LOGIQUE DE CALCUL ---
  const totaux = useMemo(() => {
    const selectedLotsData = ArrayLot.filter(lot => selectedRows.includes(lot._id));
    return selectedLotsData.reduce((acc, lot) => ({
      total: acc.total + (lot.total || 0),
      dev: acc.dev + (lot.lotCosts?.RTUD || 0),
      infra: acc.infra + (lot.lotCosts?.RTUI || 0),
      test: acc.test + (lot.lotCosts?.QC || 0),
      exec: acc.exec + (lot.lotCosts?.QE || 0),
      pilot: acc.pilot + (lot.lotCosts?.PC || 0),
      accomp: acc.accomp + (lot.lotCosts?.A || 0),
    }), { total: 0, dev: 0, infra: 0, test: 0, exec: 0, pilot: 0, accomp: 0 });
  }, [ArrayLot, selectedRows]);

  const totauxDays = useMemo(() => {
    const selectedLotsData = ArrayLot.filter(lot => selectedRows.includes(lot._id));
    return selectedLotsData.reduce((acc, lot) => {
      const tc = lot.tasksCharges;
      return {
        total: acc.total + (tc?.RTUD || 0) + (tc?.RTUI || 0) + (tc?.QC || 0) + (tc?.QE || 0) + (tc?.PC || 0) + (tc?.A || 0),
        dev: acc.dev + (tc?.RTUD || 0) + (tc?.C || 0),
        infra: acc.infra + (tc?.RTUI || 0),
        test: acc.test + (tc?.QC || 0),
        exec: acc.exec + (tc?.QE || 0),
        pilot: acc.pilot + (tc?.PC || 0),
        accomp: acc.accomp + (tc?.A || 0),
      };
    }, { total: 0, dev: 0, infra: 0, test: 0, exec: 0, pilot: 0, accomp: 0 });
  }, [ArrayLot, selectedRows]);

  // --- ACTIONS ---
  const getLotsProject = () => {
    if (token) {
      getAll(
        token, 
        `/projects/${project._id}/lots`, 
        (lots: ILot[]) => setArrayLot(lots), // On précise que c'est un tableau de ILot
        () => dispatch(sessionEnd()), 
        (err: any) => dispatch(showSnackbar("Erreur lors de la récupération", "error")) // On accepte any pour l'erreur
      );
    }
  };

  useEffect(() => { getLotsProject(); }, [project.lots]);

  useEffect(() => {
    if (ArrayLot.length > 0) setSelectedRows(ArrayLot.map(l => l._id));
  }, [ArrayLot]);

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: '#f8fafc' }}>
        <Stack direction="row" spacing={1}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport csvOptions={{ delimiter: ';', utf8WithBom: true, fileName: `Lots_${project.name}` }} />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Tooltip title={isClos ? "Le projet est clos, modification impossible depuis Kairos" : ""}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                disabled={isClos}
                sx={{ borderRadius: '10px', textTransform: 'none' }}
              >
                Nouveau lot
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </GridToolbarContainer>
    );
  }

  // --- COLONNES ---
  const commonColumns: GridColDef[] = [
    { field: "name", headerName: "Nom du lot", flex: 2, filterOperators: getGridStringOperators().filter(op => op.value === "contains") },
    { 
      field: "assignedTo", 
      headerName: "Affecté à", 
      flex: 1.3,
      renderCell: (params) => params.value 
        ? <Chip icon={<PersonIcon sx={{ fontSize: 14 }} />} label={params.value} size="small" sx={{ fontWeight: 600, bgcolor: '#eff6ff', color: '#3b82f6', borderRadius: '8px' }} />
        : <Typography variant="caption" color="text.disabled">Non affecté</Typography>,
    },
    { 
      field: isSwitchChecked ? "totalDays" : "totalCost", 
      headerName: isSwitchChecked ? "Jours Totaux" : "Coût Total", 
      flex: 1.5, 
      type: "number",
      valueFormatter: (params) => isSwitchChecked ? `${formatter2.format(params.value)} j` : formatter.format(params.value)
    },
    { field: "dev", headerName: "Dev/Conception", flex: 1, type: "number", valueFormatter: (params) => isSwitchChecked ? `${params.value} j` : formatter.format(params.value) },
    { field: "infra", headerName: "Infra", flex: 1, type: "number", valueFormatter: (params) => isSwitchChecked ? `${params.value} j` : formatter.format(params.value) },
    { field: "pilot", headerName: "Pilotage", flex: 1, type: "number", valueFormatter: (params) => isSwitchChecked ? `${params.value} j` : formatter.format(params.value) },
  ];

  const rows = ArrayLot.map(lot => ({
    id: lot._id,
    name: lot.name,
    assignedTo: lot.assignedUser ? `${lot.assignedUser.firstname || ''} ${lot.assignedUser.lastname || ''}`.trim() : null,
    totalCost: lot.total || 0,
    totalDays: (lot.tasksCharges?.RTUD || 0) + (lot.tasksCharges?.RTUI || 0) + (lot.tasksCharges?.QC || 0) + (lot.tasksCharges?.QE || 0) + (lot.tasksCharges?.PC || 0) + (lot.tasksCharges?.A || 0),
    dev: isSwitchChecked ? (lot.tasksCharges?.RTUD || 0) : (lot.lotCosts?.RTUD || 0),
    infra: isSwitchChecked ? (lot.tasksCharges?.RTUI || 0) : (lot.lotCosts?.RTUI || 0),
    pilot: isSwitchChecked ? (lot.tasksCharges?.PC || 0) : (lot.lotCosts?.PC || 0),
  }));

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* HEADER SYNTHESE */}
      <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <BarChartIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Synthèse Budgétaire</Typography>
          </Stack>
          
          <Stack direction="row" spacing={1} alignItems="center" sx={{ bgcolor: 'white', px: 2, py: 0.5, borderRadius: '20px', border: '1px solid #e2e8f0' }}>
            <EuroIcon sx={{ fontSize: 18, color: !isSwitchChecked ? 'primary.main' : 'text.disabled' }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>COÛT</Typography>
            <Switch size="small" checked={isSwitchChecked} onChange={() => setIsSwitchChecked(!isSwitchChecked)} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>CHARGE</Typography>
          </Stack>
        </Box>

        <Divider />

        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{ overflowX: 'auto' }}>
          <StatCard>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
              {isSwitchChecked ? "Total Jours" : "Budget Total"}
            </Typography>
            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 800 }}>
              {isSwitchChecked ? `${formatter2.format(totauxDays.total)} j` : formatter.format(totaux.total)}
            </Typography>
          </StatCard>
          <StatCard>
            <Typography variant="caption" color="text.secondary">Dev/Conception</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isSwitchChecked ? `${totauxDays.dev} j` : formatter.format(totaux.dev)}
            </Typography>
          </StatCard>
          <StatCard>
            <Typography variant="caption" color="text.secondary">Infra</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isSwitchChecked ? `${totauxDays.infra} j` : formatter.format(totaux.infra)}
            </Typography>
          </StatCard>
          <StatCard>
            <Typography variant="caption" color="text.secondary">Pilotage</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isSwitchChecked ? `${totauxDays.pilot} j` : formatter.format(totaux.pilot)}
            </Typography>
          </StatCard>
        </Stack>
      </Card>

      {/* GRILLE DE DONNÉES */}
      <Box sx={{ height: 500, width: '100%' }}>
        <StyledDataGrid
          rows={rows}
          columns={commonColumns}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          slots={{ toolbar: CustomToolbar, noRowsOverlay: CustomNoRowsOverlay }}
          onCellClick={(params) => params.field !== '__check__' && setLotId(params.id)}
        />
      </Box>


    </Box>
  );
}